import React, { useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Button,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Typo from './Typo';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const FeatureElement = ({
  orientation = "lr",
  title,
  image,
  description,
  listItems,
  linkLabel,
  linkUrl,
  ctaDescription,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('slide-in');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  const textBlock = (
    <Grid item xs={12} md={6}>
      <Box
      >
        <Typo
          variant="h4"
          text={title}
          sx={{
            mb: 2,
            maxWidth: '580px',
          }}
        />
        <Typo
          variant="body1"
          text={description}
          sx={{
            mb: 3,
            mt: 1,
            maxWidth: '580px',
          }}
        />

        {listItems && (
          <List sx={{ mb: 3, pl: 1 }}>
            {listItems.map((item, idx) => (
              <ListItem key={idx} sx={{ p: 0, mb: 1, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle
                    sx={{ color: '#bc0c0c', fontSize: '1.2rem' }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ marginTop: '-2px' }}
                  primary={item?.title}
                  secondary={item?.description}
                  primaryTypographyProps={{
                    sx: {
                      color: '#555',
                      lineHeight: 1.3,
                      fontWeight: 600,
                      fontSize: '1.15rem',
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      marginTop: '4px',
                      color: '#00000099',
                      lineHeight: 1.3,
                      fontWeight: 400,
                      fontSize: '0.9rem',
                      maxWidth: '580px',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Typo
          variant="body2"
          text={ctaDescription}
          sx={{
            mb: 1,
            color: '#00000099',
            fontSize: '1rem',
            maxWidth: '580px',
          }}
        />

        <Button
          variant="outlined"
          onClick={() => {
            navigate('/meet-chekup');
          }}
          sx={{
            color: '#bc0c0c',
            borderColor: '#bc0c0c',
            borderRadius: '20px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {t('planDemo')}
        </Button>
      </Box>
    </Grid>
  );

  const imageBlock = (
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          pl: 5,
          pr: 5,
        }}
      >
        <Box
          ref={imageRef}
          component="img"
          src={image}
          alt={title}
          sx={{
            width: '100%',
            opacity: 0,
            transform: 'translateX(100px)',
            transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
            '&.slide-in': {
              opacity: 1,
              transform: 'translateX(0)',
            },
          }}
        />
      </Box>
    </Grid>
  );

  return (

    <Container maxWidth={false}
      sx={{
        maxWidth: '1240px',
        px: { xs: 2, md: 4 },
        mt: 8,
        mb: 8,

      }}>
      <Box sx={{ mb: 7 }}>
        <Box
          sx={{
            mb: 5,
          }}
        >
          <Grid
            container
            sx={{ px: { xs: 5, md: 0 } }}
            spacing={5}
            direction={'row'}
            alignItems="flex-start"
          >
            {orientation === 'rl' ? (
              <>
                {imageBlock}
                {textBlock}
              </>
            ) : (
              <>
                {textBlock}
                {imageBlock}
              </>
            )}
          </Grid>
        </Box>
      </Box>
    </Container>

  );
};

export default FeatureElement;
